import { useAuth0 } from '@auth0/auth0-react';
import { useAbility } from '@casl/react';
import { faSquareInfo } from '@fortawesome/pro-light-svg-icons';
import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getAuthToken } from '../../../auth/authHelpers';
import { AbilityContext } from '../../../auth/Can';
import customerDataRequest from '../../../services/api/customer-data';
import {
    COLORS,
    SIZES,
} from '../../../constants/theme';

import Button from '../../shared/Button';

import ListingInformationDrawer from './Drawers/ListingInformationDrawer';

import styles from './AdminInfo.scss';

const AdminInfo = ({
    org,
    publicationsData,
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const ability = useAbility(AbilityContext);
    const history = useNavigate();

    const {
        is_publication_customer: isPublicationCustomer,
        salesforce_id: salesforceId,
        slug,
    } = org;
    const canAdminAll = ability.can('admin', 'all');

    const [
        isInfoDrawerOpen,
        setIsInfoDrawerOpen,
    ] = useState(false);
    const [
        isCountLoading,
        setIsCountLoading,
    ] = useState(false);
    const [
        count,
        setCount,
    ] = useState(0);

    const updateSubscriberCount = useCallback(async () => {
        const url = `/organizations/subscriber-count/${salesforceId}`;

        try {
            const token = await getAuthToken(getAccessTokenSilently);

            const customerData = await customerDataRequest('get', url, token);
            setCount(customerData.data?.data?.subscriberCount);
        } catch (_) {
            toast.error(`There was an error loading the organization's subscriber count.
            Please try again in a few minutes.`);
        }
    }, [
        getAccessTokenSilently,
        salesforceId,
    ]);

    const loadData = useCallback(async () => { // Customer data API to return subscribers counts based on salesforceId
        if (salesforceId && isPublicationCustomer) {
            setIsCountLoading(true);
            await updateSubscriberCount();
            setIsCountLoading(false);
        }
    }, [
        salesforceId,
        isPublicationCustomer,
        updateSubscriberCount,
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return (
        <>
            <div className={classNames(styles.container, 'border-section')}>
                <div className={styles.title}>
                    Admin
                </div>
                {canAdminAll && (
                    <Button
                        size={SIZES.small}
                        color={COLORS.flat}
                        onClick={() => setIsInfoDrawerOpen(true)}
                    >
                        <FontAwesomeIcon icon={faSquareInfo} />
                        {' '}
                        Listing Information
                    </Button>
                )}
                <Button
                    otherClasses={styles.accountButton}
                    onClick={() => history(`/organization/${slug}/settings`)}
                    fullWidth
                >
                    <FontAwesomeIcon icon={faGear} />
                    Manage My Listing
                </Button>
            </div>

            {/* Drawer Panels */}
            {canAdminAll && (
                <ListingInformationDrawer
                    isOpen={isInfoDrawerOpen}
                    setIsOpen={setIsInfoDrawerOpen}
                    isAdmin={canAdminAll}
                    organization={org}
                    publicationsData={publicationsData}
                    isCountLoading={isCountLoading}
                    count={count}
                />
            )}
        </>
    );
};

AdminInfo.propTypes = {
    org: PropTypes.objectOf(PropTypes.any),
    publicationsData: PropTypes.arrayOf(PropTypes.object),
};

AdminInfo.defaultProps = {
    org: {},
    publicationsData: [],
};

export default AdminInfo;
